import { api } from '../../../services/api/client';
import type { Journal, JournalSummary, JournalReport, JournalFile } from '../../../types';

export interface JournalPayload {
  instrument: string;
  trade_side: 'buy' | 'sell';
  trade_date?: string;
  time_wib?: string;
  killzone?: string;
  bias_d1?: string;
  trigger?: string;
  killer_setup?: number;
  entry_price?: number | null;
  stop_loss?: number | null;
  take_profit?: number | null;
  exit_price?: number | null;
  lot?: number | null;
  follows_rules?: boolean | null;
  emotion?: string;
  notes?: string;
  screenshot_url?: string;
  pip_size?: number | null;
  pip_value?: number | null;
}

export interface InstrumentPreset {
  symbol: string;
  label: string;
  pip_size: number;
  pip_value: number;
  contract_size: number;
}

export interface FeatureAccess {
  jurnal: boolean;
  kalkulator_lot: boolean;
  harga_pertahanan: boolean;
  request_indikator: boolean;
}

export async function fetchJournals(): Promise<Journal[]> {
  const { data } = await api.get<Journal[]>('/member/trading-journals');
  return data;
}
export async function fetchJournalSummary(group = 'all', start = '', end = ''): Promise<JournalSummary> {
  const { data } = await api.get<JournalSummary>('/member/trading-journals/stats/summary', {
    params: { group, start, end },
  });
  return data;
}
export async function fetchInstruments(): Promise<InstrumentPreset[]> {
  const { data } = await api.get<InstrumentPreset[]>('/member/trading-journals/instruments');
  return data;
}
export async function fetchJournalSettings(): Promise<{ initial_capital: number }> {
  const { data } = await api.get('/member/trading-journals/settings');
  return data;
}
export async function saveJournalSettings(initial_capital: number): Promise<{ initial_capital: number }> {
  const { data } = await api.put('/member/trading-journals/settings', { initial_capital });
  return data;
}
export async function fetchFeatureAccess(): Promise<FeatureAccess> {
  const { data } = await api.get<FeatureAccess>('/member/feature-access');
  return data;
}
export async function createJournal(payload: JournalPayload): Promise<Journal> {
  const { data } = await api.post<Journal>('/member/trading-journals', payload);
  return data;
}
export async function updateJournal(id: string, payload: Partial<JournalPayload>): Promise<Journal> {
  const { data } = await api.put<Journal>(`/member/trading-journals/${id}`, payload);
  return data;
}
export async function deleteJournal(id: string): Promise<any> {
  const { data } = await api.delete(`/member/trading-journals/${id}`);
  return data;
}
export async function addJournalFile(id: string, file_url: string): Promise<JournalFile> {
  const { data } = await api.post<JournalFile>(`/member/trading-journals/${id}/files`, { file_url, file_type: 'image' });
  return data;
}
export async function fetchJournalReport(): Promise<JournalReport> {
  const { data } = await api.get<JournalReport>('/admin/trading-journals/reports');
  return data;
}

export async function downloadJournalXlsx(): Promise<Blob> {
  const { data } = await api.get('/member/trading-journals/export/xlsx', { responseType: 'blob' });
  return data as Blob;
}
export async function downloadJournalPdf(): Promise<Blob> {
  const { data } = await api.get('/member/trading-journals/export/pdf', { responseType: 'blob' });
  return data as Blob;
}

export function fmtMoney(n?: number | null): string {
  const v = Number(n || 0);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// ---- Admin feature-access config ----
export interface FeatureAccessConfig {
  kalkulator_lot: string[];
  harga_pertahanan: string[];
  request_indikator: string[];
  all_roles: string[];
}
export async function adminGetFeatureAccess(): Promise<FeatureAccessConfig> {
  const { data } = await api.get<FeatureAccessConfig>('/admin/feature-access');
  return data;
}
export async function adminSaveFeatureAccess(
  payload: { kalkulator_lot?: string[]; harga_pertahanan?: string[]; request_indikator?: string[] },
): Promise<any> {
  const { data } = await api.put('/admin/feature-access', payload);
  return data;
}
